import { type KeyboardEvent, type ReactNode, useRef } from 'react';

export type ToolbarOrientation = 'horizontal' | 'vertical';
export type ToolbarSize = 'sm' | 'md' | 'lg';

export interface ToolbarProps {
  orientation?: ToolbarOrientation;
  size?: ToolbarSize;
  /** Landmark label. `<Toolbar>` maps to role="toolbar" so consumers
   *  should always pass a concise description of what this toolbar
   *  controls (e.g. `t('toolbar.formatting')`). */
  'aria-label': string;
  /** Alternative to aria-label when the toolbar is labelled by an on-screen
   *  heading — pass the heading's id. */
  'aria-labelledby'?: string;
  children?: ReactNode;
  className?: string;
}

export interface ToolbarGroupProps {
  /** Optional label for this segment — screen readers hear it so users
   *  can skip over groups efficiently. Visually hidden by default. */
  label?: string;
  children?: ReactNode;
  className?: string;
}

/** Editor toolbar primitive.
 *
 *  Follows WAI-ARIA toolbar pattern (https://www.w3.org/WAI/ARIA/apg/patterns/toolbar/):
 *  - Root has `role="toolbar"` with an aria-label.
 *  - Only ONE child is in the Tab order at a time (roving tabindex).
 *    Arrow keys move focus between focusable children within the
 *    toolbar's axis; Home/End jump to ends.
 *  - Groups are decorative at the DOM level — they're div wrappers for
 *    visual sectioning (with a separator border). Semantically the
 *    flat list of buttons is what matters to AT.
 *
 *  Consumers drop any focusable control (Button, IconButton, Menu
 *  trigger, Segmented) as children. Non-focusable decoration (tone
 *  labels, status text) is fine too but should be `aria-hidden`. */
function ToolbarRoot(props: ToolbarProps): ReactNode {
  const { orientation = 'horizontal', size = 'md', children, className } = props;
  const ref = useRef<HTMLDivElement>(null);

  const onKey = (e: KeyboardEvent<HTMLDivElement>): void => {
    const root = ref.current;
    if (root === null) return;
    /* Focusables = any descendant whose tabindex is 0 or -1 and which
     * isn't disabled; we manage the rove by toggling tabindex on each
     * arrow key, but for simplicity this version just re-focuses the
     * next element by DOM order. Consumers that care about rove-only
     * behaviour can wrap items in their own focus manager. */
    const items = Array.from(
      root.querySelectorAll<HTMLElement>(
        'button:not(:disabled),[role="button"]:not([aria-disabled="true"]),a[href],input:not(:disabled),[tabindex]:not([tabindex="-1"])',
      ),
    );
    if (items.length === 0) return;
    const idx = items.indexOf(document.activeElement as HTMLElement);
    const horiz = orientation === 'horizontal';
    let next = -1;
    if ((horiz && e.key === 'ArrowRight') || (!horiz && e.key === 'ArrowDown')) {
      next = idx === -1 ? 0 : (idx + 1) % items.length;
    } else if ((horiz && e.key === 'ArrowLeft') || (!horiz && e.key === 'ArrowUp')) {
      next = idx === -1 ? items.length - 1 : (idx - 1 + items.length) % items.length;
    } else if (e.key === 'Home') {
      next = 0;
    } else if (e.key === 'End') {
      next = items.length - 1;
    } else {
      return;
    }
    e.preventDefault();
    const target = items[next];
    if (target !== undefined) target.focus();
  };

  return (
    <div
      ref={ref}
      role="toolbar"
      aria-label={props['aria-label']}
      aria-labelledby={props['aria-labelledby']}
      aria-orientation={orientation}
      className={['vq-toolbar', `vq-toolbar--${orientation}`, `vq-toolbar--${size}`, className]
        .filter(Boolean)
        .join(' ')}
      onKeyDown={onKey}
      data-component="Toolbar"
      data-orientation={orientation}
    >
      {children}
    </div>
  );
}

function ToolbarGroup(props: ToolbarGroupProps): ReactNode {
  return (
    <div
      role="group"
      aria-label={props.label}
      className={['vq-toolbar__group', props.className].filter(Boolean).join(' ')}
    >
      {props.children}
    </div>
  );
}

function ToolbarSeparator(): ReactNode {
  return <span role="separator" aria-orientation="vertical" className="vq-toolbar__separator" />;
}

function ToolbarSpacer(): ReactNode {
  return <span className="vq-toolbar__spacer" aria-hidden="true" />;
}

interface ToolbarCompound {
  (props: ToolbarProps): ReactNode;
  Group: typeof ToolbarGroup;
  Separator: typeof ToolbarSeparator;
  Spacer: typeof ToolbarSpacer;
}

export const Toolbar: ToolbarCompound = Object.assign(ToolbarRoot, {
  Group: ToolbarGroup,
  Separator: ToolbarSeparator,
  Spacer: ToolbarSpacer,
});
